/*
 * The nine formatting chords (spec 12 §4.4, F-2 §7.2): Ctrl/Cmd+B bold, +I italic, +Shift+X
 * strikethrough, +Shift+8 bullet, +Shift+7 ordered, +E code, +Shift+C code block, +Shift+9 quote,
 * +Shift+U link. Link is Slack's chord and not Ctrl+K, because Ctrl+K is the quick switcher and the
 * caret lives in the composer: a chord the composer claims there is a chord the application has
 * lost. `shortcutFor` is the one table both surfaces read — the formatted editor runs the
 * action of the same name, the source view applies the markdown below. Each applier wraps or
 * prefixes the selection and restores it, and a shortcut applied to text already wrapped takes the
 * wrapping away again. Physical key codes decide every chord, because Shift+8 is `(` on a German
 * keyboard and `*` on an American one.
 */

import type { Edit } from "./edit.ts"

export type Shortcut =
	"bold" | "italic" | "strike" | "bullet" | "ordered" | "code" | "codeblock" | "quote" | "link"

export interface KeyLike {
	code: string
	ctrlKey: boolean
	metaKey: boolean
	shiftKey: boolean
	altKey: boolean
}

/** One chord: a physical key code and whether Shift is part of it; Ctrl or Cmd always is. */
export interface Chord {
	code: string
	shift: boolean
}

/**
 * The table itself, in the order the toolbar draws the actions. `shortcutFor` reads it, the editor
 * derives its tooltips and `aria-keyshortcuts` from it, and the source view applies it — one place
 * to change a chord.
 */
export const SHORTCUT_CHORDS = {
	bold: { code: "KeyB", shift: false },
	italic: { code: "KeyI", shift: false },
	strike: { code: "KeyX", shift: true },
	bullet: { code: "Digit8", shift: true },
	ordered: { code: "Digit7", shift: true },
	code: { code: "KeyE", shift: false },
	codeblock: { code: "KeyC", shift: true },
	quote: { code: "Digit9", shift: true },
	link: { code: "KeyU", shift: true },
} as const satisfies Record<Shortcut, Chord>

export const SHORTCUTS = Object.keys(SHORTCUT_CHORDS) as Shortcut[]

export function shortcutFor(e: KeyLike): Shortcut | null {
	if (!(e.ctrlKey || e.metaKey) || e.altKey) return null
	return (
		SHORTCUTS.find((id) => {
			const chord = SHORTCUT_CHORDS[id]
			return chord.code === e.code && chord.shift === e.shiftKey
		}) ?? null
	)
}

/** The key a code names on a keycap: `KeyB` is B, `Digit8` is 8. */
export function keyOfCode(code: string): string {
	return code.replace(/^(Key|Digit)/, "")
}

/** The words a shortcut inserts when nothing is selected, already translated. */
export interface Placeholders {
	bold: string
	italic: string
	strike: string
	code: string
	linkText: string
	url: string
}

/** Wraps the selection, or unwraps it when it already is; an empty selection gets a placeholder. */
export function wrap(
	text: string,
	start: number,
	end: number,
	prefix: string,
	suffix: string,
	placeholder: string,
): Edit {
	const wrapped =
		text.slice(Math.max(0, start - prefix.length), start) === prefix &&
		text.slice(end, end + suffix.length) === suffix
	if (wrapped) {
		return {
			text:
				text.slice(0, start - prefix.length) +
				text.slice(start, end) +
				text.slice(end + suffix.length),
			start: start - prefix.length,
			end: end - prefix.length,
		}
	}
	const inner = start === end ? placeholder : text.slice(start, end)
	return {
		text: text.slice(0, start) + prefix + inner + suffix + text.slice(end),
		start: start + prefix.length,
		end: start + prefix.length + inner.length,
	}
}

/**
 * Prefixes every line the selection touches with a marker, numbering an ordered list; when every
 * line already carries the marker, takes it away.
 */
export function prefixLines(
	text: string,
	start: number,
	end: number,
	kind: "bullet" | "ordered" | "quote",
): Edit {
	const firstLine = text.lastIndexOf("\n", start - 1) + 1
	const nl = text.indexOf("\n", end)
	const lastLineEnd = nl === -1 ? text.length : nl
	const lines = text.slice(firstLine, lastLineEnd).split("\n")
	const pattern = kind === "bullet" ? /^- / : kind === "ordered" ? /^\d+\. / : /^> /
	const remove = lines.every((line) => pattern.test(line))
	let firstDelta = 0
	let totalDelta = 0
	const changed = lines.map((line, index) => {
		const next = remove
			? line.replace(pattern, "")
			: `${kind === "bullet" ? "- " : kind === "ordered" ? `${index + 1}. ` : "> "}${line}`
		const delta = next.length - line.length
		if (index === 0) firstDelta = delta
		totalDelta += delta
		return next
	})
	return {
		text: text.slice(0, firstLine) + changed.join("\n") + text.slice(lastLineEnd),
		start: Math.max(firstLine, start + firstDelta),
		end: Math.max(firstLine, end + totalDelta),
	}
}

/**
 * `[text](url)`: a selection becomes the link text and the URL placeholder is selected for typing;
 * with nothing selected the text placeholder is selected instead.
 */
export function link(text: string, start: number, end: number, p: Placeholders): Edit {
	const selected = text.slice(start, end)
	const label = selected === "" ? p.linkText : selected
	const insert = `[${label}](${p.url})`
	const next = text.slice(0, start) + insert + text.slice(end)
	if (selected === "") return { text: next, start: start + 1, end: start + 1 + label.length }
	const urlAt = start + 1 + label.length + 2
	return { text: next, start: urlAt, end: urlAt + p.url.length }
}

/**
 * Puts the lines the selection touches into a fenced block on lines of their own, or takes the
 * fences away again when the lines are already inside one. The selection keeps covering the same
 * text, so a second press is the exact inverse of the first.
 */
export function fenceLines(text: string, start: number, end: number): Edit {
	const firstLine = text.lastIndexOf("\n", start - 1) + 1
	const nl = text.indexOf("\n", end)
	const lastLineEnd = nl === -1 ? text.length : nl
	const before = text.slice(0, firstLine)
	const after = text.slice(lastLineEnd)
	const openAt = before.lastIndexOf("```\n")
	const opened = openAt !== -1 && openAt === before.length - 4
	const closed = after.startsWith("\n```")
	if (opened && closed) {
		return {
			text: before.slice(0, openAt) + text.slice(firstLine, lastLineEnd) + after.slice(4),
			start: start - 4,
			end: end - 4,
		}
	}
	return {
		text: `${before}\`\`\`\n${text.slice(firstLine, lastLineEnd)}\n\`\`\`${after}`,
		start: start + 4,
		end: end + 4,
	}
}

export function applyShortcut(
	s: Shortcut,
	text: string,
	start: number,
	end: number,
	p: Placeholders,
): Edit {
	switch (s) {
		case "bold":
			return wrap(text, start, end, "**", "**", p.bold)
		case "italic":
			return wrap(text, start, end, "_", "_", p.italic)
		case "strike":
			return wrap(text, start, end, "~~", "~~", p.strike)
		case "code":
			return wrap(text, start, end, "`", "`", p.code)
		case "codeblock":
			return fenceLines(text, start, end)
		case "bullet":
		case "ordered":
		case "quote":
			return prefixLines(text, start, end, s)
		case "link":
			return link(text, start, end, p)
	}
}
